// Google Gemini API Client
// https://ai.google.dev/gemini-api/docs

import type { HttpRequest } from '../http.js'
import { createRequestAPI } from './utils.js'

export interface GeminiGenerateContentArgs {
  apiKey?: string
  model?: string
  contents: unknown[]
  generationConfig?: Record<string, unknown>
  safetySettings?: unknown[]
  tools?: unknown[]
  toolConfig?: unknown
  systemInstruction?: unknown
}

export interface GeminiChatArgs {
  apiKey?: string
  model?: string
  messages: Array<{ role: string; content: string }>
  temperature?: number
  maxOutputTokens?: number
  topP?: number
  topK?: number
}

export interface GeminiClient {
  generateContent(args: GeminiGenerateContentArgs): Promise<any>
  listModels(args: { apiKey?: string; pageSize?: number; pageToken?: string }): Promise<any>
  getModel(args: { apiKey?: string; model: string }): Promise<any>
  chat(args: GeminiChatArgs): Promise<any>
}

export function createGeminiClient(httpRequest: HttpRequest): GeminiClient {
  const requestAPI = createRequestAPI(httpRequest)

  const generateContent = async ({
    apiKey,
    model = 'gemini-2.5-flash',
    contents,
    generationConfig,
    safetySettings,
    tools,
    toolConfig,
    systemInstruction,
  }: GeminiGenerateContentArgs) => {
    if (!contents || !Array.isArray(contents) || contents.length === 0) {
      throw new Error('contents is required and must be a non-empty array')
    }

    const bodyParams = {
      contents,
      generationConfig,
      safetySettings,
      tools,
      toolConfig,
      systemInstruction,
    }

    const body = Object.fromEntries(
      Object.entries(bodyParams).filter(([_, value]) => value !== undefined),
    )

    return await requestAPI({
      path: `/v1beta/models/${model}:generateContent`,
      method: 'POST',
      apiKey,
      body,
    })
  }

  const listModels = async ({
    apiKey,
    pageSize,
    pageToken,
  }: { apiKey: string; pageSize?: number; pageToken?: string }) => {
    let path = '/v1beta/models'
    const params: string[] = []

    if (pageSize) params.push(`pageSize=${pageSize}`)
    if (pageToken) params.push(`pageToken=${pageToken}`)

    if (params.length > 0) path += `?${params.join('&')}`

    return await requestAPI({ path, method: 'GET', apiKey, body: {} })
  }

  const getModel = async ({ apiKey, model }: { apiKey: string; model: string }) => {
    if (!model) throw new Error('model is required')
    return await requestAPI({
      path: `/v1beta/models/${model}`,
      method: 'GET',
      apiKey,
      body: {},
    })
  }

  const chat = async ({
    apiKey,
    model = 'gemini-2.5-flash',
    messages,
    temperature,
    maxOutputTokens,
    topP,
    topK,
  }: GeminiChatArgs) => {
    if (!messages || !Array.isArray(messages) || messages.length === 0) {
      throw new Error('messages is required and must be a non-empty array')
    }

    const contents = messages.map(msg => ({
      role: msg.role === 'system' ? 'user' : msg.role,
      parts: [{ text: msg.content }],
    }))

    const generationConfig: Record<string, unknown> = {}
    if (temperature !== undefined) generationConfig.temperature = temperature
    if (maxOutputTokens !== undefined) generationConfig.maxOutputTokens = maxOutputTokens
    if (topP !== undefined) generationConfig.topP = topP
    if (topK !== undefined) generationConfig.topK = topK

    return await generateContent({
      apiKey,
      model,
      contents,
      generationConfig: Object.keys(generationConfig).length > 0 ? generationConfig : undefined,
    })
  }

  return { generateContent, listModels, getModel, chat }
}
